import { useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styles from './PostCard.module.css';
import TranslateToggle from './TranslateToggle';
import { useTranslation } from 'react-i18next';

const PostCard = ({ post, canDelete = false, onDelete, currentUserId = '', onToggleLike, isDetailView = false }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const username = post?.username?.trim() || '';
  const displayName = username || post?.displayName || post?.userId || 'Anonymous hiker';
  const avatarUrl = post?.authorPfp || '';

  const timestamp = useMemo(() => {
    if (post?.timestamp?.toDate) {
      return post.timestamp.toDate();
    }
    if (post?.timestamp?._seconds) {
      return new Date(post.timestamp._seconds * 1000);
    }
    if (typeof post?.timestamp === 'number') {
      return new Date(post.timestamp);
    }
    if (typeof post?.timestamp === 'string') {
      const parsed = new Date(post.timestamp);
      return Number.isNaN(parsed.getTime()) ? null : parsed;
    }
    return null;
  }, [post?.timestamp]);

  const likesMap = post?.likes || {};
  const likeCount = Object.keys(likesMap).length;
  const isLiked = currentUserId ? Boolean(likesMap[currentUserId]) : false;
  const profileUrl = username ? `/profile/${encodeURIComponent(username)}` : '';

  const handleCardClick = (e) => {
    if (isDetailView) return;
    // Don't navigate if text was selected
    if (window.getSelection().toString().length > 0) return;
    
    navigate(`/post/${post.id}`);
  };

  const stopProp = (e) => {
    e.stopPropagation();
  };

  const truncatedText = useMemo(() => {
    const text = post?.postText || '';
    if (isDetailView) return text;
    if (text.length > 250) {
      return text.substring(0, 250).trim() + '...';
    }
    return text;
  }, [post?.postText, isDetailView]);

  const hasMoreText = !isDetailView && (post?.postText || '').length > 250;

  return (
    <article 
      className={`${styles.card} ${!isDetailView ? styles.clickable : ''}`} 
      onClick={handleCardClick}
    >
      <header className={styles.header}>
        <div className={styles.authorBlock}>
          <div className={styles.avatar}>
            {avatarUrl ? (
              <img src={avatarUrl} alt={`Avatar of ${displayName || 'user'}`} />
            ) : (
              displayName.charAt(0).toUpperCase()
            )}
          </div>
          <div>
            <p className={styles.username}>
              {profileUrl ? (
                <Link to={profileUrl} className={styles.usernameLink} onClick={stopProp}>
                  {username}
                </Link>
              ) : (
                displayName
              )}
            </p>
            {timestamp && (
              <time className={styles.timestamp} dateTime={timestamp.toISOString()}>
                {timestamp.toLocaleString()}
              </time>
            )}
          </div>
        </div>
        {canDelete && (
          <button type="button" className={styles.deleteButton} onClick={(e) => { stopProp(e); onDelete(); }}>
            Delete
          </button>
        )}
      </header>

      <div className={styles.content}>
        <p className={styles.body}>
          {truncatedText}
          {hasMoreText && (
            <span className={styles.seeMore}> {t('general.seeMore') || 'See more'}</span>
          )}
        </p>
        <div onClick={stopProp}>
            <TranslateToggle text={post?.postText || ''} />
        </div>

        {(post?.mountainTags?.length > 0 || post?.gearTags?.length > 0) && (
          <div className={styles.tags}>
            {post?.mountainTags?.map((tag) => (
              <Link
                key={`mount-${tag.id}`}
                to={`/mountains/${encodeURIComponent(tag.id)}`}
                className={`${styles.tag} ${styles.mountainTag} ${styles.tagLink}`}
                onClick={stopProp}
              >
                {tag.name}
              </Link>
            ))}
            {post?.gearTags?.map((tag) => (
              <Link
                key={`gear-${tag.id}`}
                to={`/gear/${encodeURIComponent(tag.id)}`}
                className={`${styles.tag} ${styles.gearTag} ${styles.tagLink}`}
                onClick={stopProp}
              >
                {tag.name}
              </Link>
            ))}
          </div>
        )}
      </div>

      {post?.imageUrl && isDetailView && (
        <div className={styles.imageWrapper}>
          <img src={post.imageUrl} alt="Post attachment" loading="lazy" />
        </div>
      )}

      <footer className={styles.footer}>
        <button
          className={styles.commentButton}
          type="button"
          onClick={handleCardClick}
        >
          <svg style={{width:'1.25rem', height: '1.25rem'}} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 20.25c4.97 0 9-3.694 9-8.25s-4.03-8.25-9-8.25S3 7.444 3 12c0 2.104.859 4.023 2.273 5.48.432.447.74 1.04.586 1.641a4.483 4.483 0 0 1-.923 1.785A5.969 5.969 0 0 0 6 21c1.282 0 2.47-.402 3.445-1.087.81.22 1.668.337 2.555.337Z" />
          </svg>
        </button>
        <button
          type="button"
          className={`${styles.likeButton} ${isLiked ? styles.likeButtonActive : ''}`}
          onClick={(e) => { 
            stopProp(e); 
            if (onToggleLike) onToggleLike(post);
          }}
          disabled={!currentUserId}
        >
          <span className={styles.heartIcon}>{isLiked ? '♥' : '♡'}</span>
          <span className={styles.countLabel}>{likeCount}</span>
        </button>
      </footer>
    </article>
  );
};

export default PostCard;
